import {apiClient} from '@common/http/query-client';
import {useStudentView} from './student-view';
import {useMutation} from '@tanstack/react-query';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {toast} from '@ui/toast/toast';
import {message} from '@ui/i18n/message';
import {PersonIcon} from '@ui/icons/material/Person';
import {LogoutIcon} from '@ui/icons/material/Logout';
import {useLayoutEffect, useRef} from 'react';

/**
 * The way back, while signed in as somebody else.
 *
 * "Log in as this user" on the customer page really does sign you in as them:
 * `auth()` returns the customer, every page behaves as theirs, and nothing on
 * screen said otherwise. The only exit was to sign out and sign in again — and
 * the endpoint that was supposed to end it had no route, and logged you out
 * rather than putting you back.
 *
 * So this sits above every page, not just the portal: an impersonated session
 * can wander anywhere on the site, and being stranded on the homepage as
 * somebody else is exactly the situation it exists to end.
 */
export function ImpersonationBar() {
  const {impersonating} = useStudentView();

  const stop = useMutation({
    mutationFn: () =>
      apiClient
        .post<{restored: boolean}>('admin/users/stop-impersonating')
        .then(r => r.data),
    // A full navigation, not a route change: the identity behind every cached
    // query just changed, and reloading is the only way to be sure none of the
    // impersonated account's data survives on screen.
    onSuccess: data => {
      window.location.href = data?.restored ? '/admin/customers' : '/login';
    },
    onError: () => toast.danger(message('Could not switch back. Try signing out.')),
  });

  // Publish this bar's height so the full-height layouts can subtract it.
  //
  // The portal shells are `100dvh` tall. Sitting a bar above one of them made
  // the page taller than the window, and because those shells scroll INSIDE
  // themselves rather than moving the body, the bottom simply became
  // unreachable — no footer, no way down. Measured rather than hardcoded: the
  // bar wraps to two lines on a narrow screen.
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    const root = document.documentElement;

    if (!el) {
      root.style.removeProperty('--app-banner-h');
      return;
    }

    const publish = () => root.style.setProperty('--app-banner-h', `${el.offsetHeight}px`);
    publish();

    const observer = new ResizeObserver(publish);
    observer.observe(el);

    return () => {
      observer.disconnect();
      root.style.removeProperty('--app-banner-h');
    };
  }, [impersonating]);

  if (!impersonating) return null;

  return (
    <div
      ref={ref}
      className="flex flex-wrap items-center justify-between gap-x-16 gap-y-8 bg-warning px-16 py-8 text-sm text-white"
    >
      <div className="flex min-w-0 items-center gap-10">
        <PersonIcon size="sm" className="shrink-0" />
        <span className="min-w-0">
          <Trans message="You are signed in as this user." />{' '}
          <span className="opacity-90">
            <Trans
              message="Anything you do here is done as them, not as :name."
              values={{name: impersonating.name}}
            />
          </span>
        </span>
      </div>

      <Button
        variant="outline"
        size="xs"
        className="!border-white/60 !text-white hover:!bg-white/15"
        startIcon={<LogoutIcon />}
        disabled={stop.isPending}
        onClick={() => stop.mutate()}
      >
        <Trans message="Back to my account" />
      </Button>
    </div>
  );
}
